// WeakMap to store original server-injected nonce values
const preservedNonces = new WeakMap<Element, string>();

/**
 * Gets nonce value for CSP from meta tag
 * Standard Angular CSP pattern - single source of truth
 */
export function getNonce(element?: HTMLElement): string | undefined {
  return getNonceFromCSPHeader();
}

/**
 * Preserves server-injected nonce values before Angular hydration overwrites them
 * Called early in app initialization using standard Angular CSP patterns
 */
export function preserveExistingNonceValues(): void {
  const metaNonce = getNonceFromCSPHeader();
  if (!metaNonce) {
    return;
  }

  // Store server-injected nonces before Angular overwrites them
  preserveServerNonces();
  
  // Set up standard event-driven monitoring for DOM changes
  setupMutationObserver();
}

/**
 * Store original server nonce values before Angular hydration
 * Standard pattern: preserve server state before framework takes over
 */
function preserveServerNonces(): void {
  const metaNonce = getNonceFromCSPHeader();
  if (!metaNonce) {
    return;
  }

  // Find all script/style elements with nonce attributes (server-injected)
  const nonceElements = document.querySelectorAll('script[nonce], style[nonce]');
  
  nonceElements.forEach((element) => {
    const currentNonce = element.getAttribute('nonce');
    
    // Store the server nonce value (whether populated or empty)
    // Angular will overwrite both, so we preserve the intended value
    if (currentNonce !== null) {
      preservedNonces.set(element, metaNonce);
      
      // Ensure element has the correct nonce now
      element.setAttribute('nonce', metaNonce);
    }
  });
}

/**
 * Standard MutationObserver pattern for Angular CSP
 * Event-driven monitoring without periodic scanning
 */
function setupMutationObserver(): void {
  if (typeof MutationObserver === 'undefined') {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Restore server nonces when Angular overwrites them
      if (mutation.type === 'attributes' && mutation.attributeName === 'nonce') {
        const element = mutation.target as HTMLElement;
        const preservedNonce = preservedNonces.get(element);
        
        if (preservedNonce && element.getAttribute('nonce') !== preservedNonce) {
          element.setAttribute('nonce', preservedNonce);
        }
      }
      
      // Apply nonces to newly added elements
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            applyNonceToNewElement(element);
          }
        });
      }
    });
  });
  
  // Standard Angular pattern: monitor attributes and child additions
  observer.observe(document.documentElement, { 
    attributes: true, 
    attributeFilter: ['nonce'], 
    childList: true,
    subtree: true 
  });
}

/**
 * Apply nonce to newly added script/style element
 * Standard Angular CSP pattern for dynamic content
 */
function applyNonceToNewElement(element: HTMLElement): void {
  const metaNonce = getNonceFromCSPHeader();
  if (!metaNonce) {
    return;
  }

  // Apply to the element if it's a script or style without a nonce
  if ((element.tagName === 'SCRIPT' || element.tagName === 'STYLE') && !element.getAttribute('nonce')) {
    element.setAttribute('nonce', metaNonce);
  }
  
  // Apply to nested script/style elements
  const nestedElements = element.querySelectorAll('script:not([nonce]), style:not([nonce])');
  nestedElements.forEach((nestedEl) => {
    nestedEl.setAttribute('nonce', metaNonce);
  });
}

/**
 * Standard Angular CSP utility to apply nonce to an element
 * Maintains backward compatibility with existing code
 */
export function applyNonceToElement(element: HTMLElement): void {
  const metaNonce = getNonceFromCSPHeader();
  if (!metaNonce) {
    return;
  }
  
  // Don't overwrite existing nonces unless empty
  const existingNonce = element.getAttribute('nonce');
  if (existingNonce && existingNonce.trim() !== '') {
    return;
  }
  
  // Apply meta tag nonce - standard Angular pattern
  element.setAttribute('nonce', metaNonce);
}

const getNonceFromCSPHeader = (): string | undefined => {
  return document.querySelector('meta[name="csp-nonce"]')?.getAttribute('value') || undefined;
};
